import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { useState } from "react";
import { Shield, FileCheck, Lock } from "lucide-react";

export default function NDAPage() {
  const [accepted, setAccepted] = useState(false);
  const [loading, setLoading] = useState(false);
  const { acceptNDA, user } = useAuth();
  const navigate = useNavigate();

  const handleAccept = async () => {
    if (!accepted) {
      toast.error("Please accept the NDA to continue");
      return;
    }
    
    setLoading(true);
    try {
      await acceptNDA();
      toast.success("NDA accepted successfully");
      navigate("/dashboard");
    } catch (error) {
      toast.error("Failed to accept NDA");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-8">
      <div className="max-w-2xl w-full bg-white border border-slate-200 p-8 lg:p-12 animate-slide-in">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8 pb-6 border-b border-slate-200">
          <div className="w-14 h-14 bg-slate-900 flex items-center justify-center">
            <Shield className="w-7 h-7 text-white" />
          </div>
          <div>
            <h1 className="font-heading text-2xl font-bold text-slate-900 uppercase tracking-tight" data-testid="nda-title">
              Non-Disclosure Agreement
            </h1>
            <p className="text-slate-500 text-sm">Required before accessing the platform</p>
          </div>
        </div>

        {/* Content */}
        <div className="space-y-6 text-slate-600 text-sm leading-relaxed">
          <div className="flex gap-3">
            <Lock className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" />
            <p>
              <strong className="text-slate-900">Confidentiality:</strong> All design files, pricing information, 
              and technical specifications shared through the RIMCORE PRECUT platform are strictly confidential 
              and remain the intellectual property of the respective parties.
            </p>
          </div>

          <div className="flex gap-3">
            <FileCheck className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" />
            <p>
              <strong className="text-slate-900">Usage Restrictions:</strong> You agree not to share, reproduce, 
              or distribute any proprietary information, design files, or pricing data obtained through this 
              platform without explicit written consent.
            </p>
          </div>

          <div className="flex gap-3">
            <Shield className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" />
            <p>
              <strong className="text-slate-900">Data Protection:</strong> We employ industry-standard encryption 
              and security measures to protect your data. You agree to maintain the security of your account 
              credentials and report any unauthorized access immediately.
            </p>
          </div>

          <div className="bg-slate-50 p-4 border-l-4 border-orange-500 mt-8">
            <p className="text-xs uppercase tracking-wider text-slate-500 mb-2">Legal Notice</p>
            <p className="text-slate-700">
              This agreement is legally binding. By accepting, you acknowledge that you have read, understood, 
              and agree to be bound by these terms. Violation may result in legal action and termination of access.
            </p>
          </div>
        </div>

        {/* Accept Section */}
        <div className="mt-8 pt-6 border-t border-slate-200">
          <div className="flex items-start gap-3 mb-6">
            <Checkbox
              id="accept-nda"
              checked={accepted}
              onCheckedChange={setAccepted}
              className="mt-0.5 rounded-none"
              data-testid="nda-checkbox"
            />
            <label htmlFor="accept-nda" className="text-sm text-slate-700 cursor-pointer">
              I, <strong>{user?.full_name || "User"}</strong>, have read and agree to the terms of this 
              Non-Disclosure Agreement and understand my obligations regarding confidentiality and data protection.
            </label>
          </div>

          <Button
            onClick={handleAccept}
            disabled={!accepted || loading}
            className="w-full h-12 rounded-none bg-slate-900 text-white font-heading uppercase tracking-wider hover:bg-slate-800 disabled:opacity-50 btn-industrial"
            data-testid="accept-nda-button"
          >
            {loading ? "Processing..." : "Accept & Continue"}
          </Button>
        </div>
      </div>
    </div>
  );
}
